<template>
  <v-form class="px-2" v-model="valid" ref="winnerForm">
    <h3 class="display-1 font-weight-bold">Winners</h3>
    <v-alert :value="limits.played" type="warning" outline class="my-3" style="border-radius: 0.5rem;">
      {{limits.playedMessage}}
    </v-alert>
    <v-layout>
      <v-select
          v-model="type"
          :items="typeItems"
          :rules="[v => !!v || 'required']"
          label="Instaplay type"
          :readonly="limits.played"
      ></v-select>
    </v-layout>

    <div v-if="type===typeItems[0].value">
      <v-layout row wrap>
        <v-flex xs12 md5>
          <v-subheader class="px-0">What is the prize?</v-subheader>
        </v-flex>
        <v-flex xs12 md7>
          <v-radio-group v-model="prize" column>
            <v-layout align-center>
              <v-radio :readonly="limits.played" label="SCoins" value="scoins"></v-radio>
              <v-text-field
                  v-model.number="winSCoins"
                  :rules="posNumRule"
                  required
                  v-if="prize==='scoins'"
                  type="number"
                  min="0"
                  :readonly="limits.played"
              ></v-text-field>
            </v-layout>
            <v-layout align-center>
              <v-radio :readonly="limits.played" label="Custom #" value="custom"></v-radio>
              <v-text-field
                  v-model.number="hashtag"
                  :rules="posNumRule"
                  required
                  v-if="prize==='custom'"
                  type="number"
                  min="0"
                  :readonly="limits.played"
              ></v-text-field>
            </v-layout>
            <v-layout align-center pl-4 ml-2 v-if="prize==='custom'"
            >
              <v-text-field
                  label="Description"
                  v-model="description"
                  type="text"
                  :readonly="limits.played"
              ></v-text-field>
              <v-layout row align-center>
                <v-avatar
                    v-if="customImage.url"
                    :size="50"
                    class="mx-2"
                >
                  <img :src="customImage.url" :alt="customImage.name">
                </v-avatar>
                <image-input
                    accept="image/*"
                    @file-update="(file) => { update(file, customImage) }"
                    :show-remove="!!customImage.name"
                    round
                    flat
                    small
                ></image-input>
              </v-layout>
            </v-layout>
          </v-radio-group>

        </v-flex>
      </v-layout>

      <v-layout row wrap class="align-center">
        <v-flex xs12 md5>
          <v-subheader class="px-0">How many winners are there?</v-subheader>
        </v-flex>
        <v-layout row wrap>
          <v-text-field
              label="Count"
              v-model.number="numWinners"
              :disabled="noLimit"
              :rules="[v => noLimit || !!v || 'required', v => noLimit || v>0 || 'count must be positive']"
              min="1"
              type="number"
              :readonly="limits.played"
          ></v-text-field>
          <span class="pa-4">OR</span>
          <v-checkbox
              v-model="noLimit"
              label="No limit"
              :rules="[v => !!v]"
              @change="$refs.winnerForm.validate()"
              :readonly="limits.played"
          ></v-checkbox>
        </v-layout>
      </v-layout>

      <v-layout row wrap>
        <v-flex xs12 md5>
          <v-subheader class="px-0">How is the winner selected?</v-subheader>
        </v-flex>
        <v-flex xs12 md7>
          <v-radio-group v-model="winnerSelection" :rules="[v => !!winnerSelection || !!v]" required>
            <v-radio :readonly="limits.played" :label="'First '+(noLimit? '': numWinners)+' player(s) to select the winning contestant'"
                     value="first"></v-radio>
            <v-radio :readonly="limits.played" label="Arbitrary by GameMaster" value="arbitrary"></v-radio>
          </v-radio-group>
        </v-flex>
      </v-layout>
    </div>

    <div v-else-if="type===typeItems[1].value">
      <v-layout row wrap class="align-center">
        <v-flex xs12 md5>
          <v-subheader class="px-0">How many SCoins does a player contribute?</v-subheader>
        </v-flex>
        <v-flex xs6 md3>
          <v-text-field
              label="Minimum SCoins"
              v-model.number="minCoinsToPlay"
              :rules="posNumRule"
              required
              type="number"
              min="0"
              :readonly="limits.played"
          ></v-text-field>
        </v-flex>
        <v-flex xs6 md3>
          <v-text-field
              label="Maximum SCoins"
              v-model.number="maxCoinsToPlay"
              :rules="posNumRule"
              required
              type="number"
              :min="minCoinsToPlay || 0"
              @keydown="wrongTry = maxCoinsToPlay === minCoinsToPlay"
              class="pl-3"
              :readonly="limits.played"
          ></v-text-field>
        </v-flex>
        <p class="error--text v-messages" v-if="!poolConstraint">
          Maximum must be greater than or equal to minimum.
        </p>
      </v-layout>
      <v-layout row wrap class="align-center">
        <v-flex xs12 md5>
          <v-subheader class="px-0">Number of SCoins contributed by the Game Master:</v-subheader>
        </v-flex>
        <v-flex xs12 md7>
          <v-text-field
              label="SCoins"
              v-model.number="poolSCoinGM"
              :rules="posNumRule"
              required
              type="number"
              :readonly="limits.played"
          ></v-text-field>
        </v-flex>
      </v-layout>
    </div>

    <div v-else-if="type===typeItems[2].value">
      <v-layout row wrap class="align-center">
        <v-flex xs12 md5>
          <v-subheader class="px-0">Total points:</v-subheader>
        </v-flex>
        <v-flex xs12 md7>
          <v-text-field
              label="points"
              v-model.number="totalpoints"
              :rules="posNumRule"
              required
              type="number"
              min="0"
              :readonly="limits.played"
          ></v-text-field>
        </v-flex>
      </v-layout>
      <v-layout row wrap class="align-center">
        <v-flex xs12 md5>
          <v-subheader class="px-0">SCoins wagered by the game master:</v-subheader>
        </v-flex>
        <v-flex xs12 md7>
          <v-text-field
              label="SCoins"
              v-model.number="totalSCoinGM"
              :rules="posNumRule"
              required
              type="number"
              min="0"
              :readonly="limits.played"
          ></v-text-field>
        </v-flex>
      </v-layout>
      <v-layout>
        <v-subheader class="px-0">
          Player wagers
          <v-text-field
              v-model.number="wagerSCoins"
              :rules="[v => !!v || 'required', v => v>=10 || 'May not be less than 10']"
              required
              type="number"
              min="10"
              class="px-2 font-weight-regular"
              :readonly="limits.played"
          ></v-text-field>
          SCoins to win 10 SCoins.
        </v-subheader>
      </v-layout>
    </div>

    <div v-else-if="type===typeItems[3].value">
      <v-layout row wrap class="align-center">
        <v-flex xs12 md5>
          <v-subheader class="px-0">SCoins to select the favored ({{favoredContestant.name}}) contestant:</v-subheader>
        </v-flex>
        <v-flex xs12 md7>
          <v-text-field
              label="SCoins"
              v-model.number="SCoinFavored"
              :rules="posNumRule"
              required
              type="number"
              min="0"
              :readonly="limits.played"
          ></v-text-field>
        </v-flex>
      </v-layout>
      <v-layout row wrap class="align-center">
        <v-flex xs12 md5>
          <v-subheader class="px-0">SCoins awarded if the underdog ({{underdogContestant.name}}) contestant wins:
          </v-subheader>
        </v-flex>
        <v-flex xs12 md7>
          <v-text-field
              label="SCoins"
              v-model.number="SCoinUnderdog"
              :rules="posNumRule"
              required
              type="number"
              min="0"
              :readonly="limits.played"
          ></v-text-field>
        </v-flex>
      </v-layout>
      <v-layout row wrap class="align-center">
        <v-flex xs12 md5>
          <v-subheader class="px-0">SCoins wagered by the game master:</v-subheader>
        </v-flex>
        <v-flex xs12 md7>
          <v-text-field
              label="SCoins"
              v-model.number="SCoinGM"
              :rules="posNumRule"
              required
              type="number"
              min="0"
              :readonly="limits.played"
          ></v-text-field>
        </v-flex>
      </v-layout>
    </div>

    <div v-else-if="type===typeItems[4].value">
      <v-layout row wrap class="align-center">
        <v-flex xs12 md5>
          <v-subheader class="px-0">Point spread between the favored ({{favoredContestant.name}}) and the underdog
            ({{underdogContestant.name}}):
          </v-subheader>
        </v-flex>
        <v-flex xs12 md7>
          <v-text-field
              label="points"
              v-model.number="pointSpread"
              :rules="posNumRule"
              required
              type="number"
              min="0"
              :readonly="limits.played"
          ></v-text-field>
        </v-flex>
      </v-layout>
      <v-layout row wrap class="align-center">
        <v-flex xs12 md5>
          <v-subheader class="px-0">SCoins wagered by the game master:</v-subheader>
        </v-flex>
        <v-flex xs12 md7>
          <v-text-field
              label="SCoins"
              v-model.number="spreadSCoinGM"
              :rules="posNumRule"
              required
              type="number"
              min="0"
              :readonly="limits.played"
          ></v-text-field>
        </v-flex>
      </v-layout>
      <v-layout>
        <v-subheader class="px-0">
          Player wagers
          <v-text-field
                  v-model.number="spreadWagerSCoins"
                  :rules="[v => !!v || 'required', v => v>=10 || 'May not be less than 10']"
                  required
                  type="number"
                  min="10"
                  class="px-2 font-weight-regular"
                  :readonly="limits.played"
          />
          SCoins to win 10 SCoins.
        </v-subheader>
      </v-layout>
    </div>

    <div v-else-if="type===typeItems[5].value">
      <bracket-item :teams="cards" :all-teams="cards" :model="bracketModel" for-create root >
        <v-text-field
                label="Label for the winner"
                v-model="winnerLabel"
        />
      </bracket-item>
      <v-layout row wrap class="align-center">
        <v-flex xs12 md5>
          <v-subheader class="px-0">How many SCoins does a player contribute?</v-subheader>
        </v-flex>
        <v-flex xs6 md3>
          <v-text-field
                  label="Minimum SCoins"
                  v-model.number="minCoinsToPlay"
                  :rules="posNumRule"
                  required
                  type="number"
                  min="0"
                  :readonly="limits.played"
          ></v-text-field>
        </v-flex>
        <v-flex xs6 md3>
          <v-text-field
                  label="Maximum SCoins"
                  v-model.number="maxCoinsToPlay"
                  :rules="posNumRule"
                  required
                  type="number"
                  :min="minCoinsToPlay || 0"
                  @keydown="wrongTry = maxCoinsToPlay === minCoinsToPlay"
                  class="pl-3"
                  :readonly="limits.played"
          ></v-text-field>
        </v-flex>
        <p class="error--text v-messages" v-if="!poolConstraint">
          Maximum must be greater than or equal to minimum.
        </p>
      </v-layout>
      <v-layout row wrap class="align-center">
        <v-flex xs12 md5>
          <v-subheader class="px-0">Number of SCoins contributed by the Game Master:</v-subheader>
        </v-flex>
        <v-flex xs12 md7>
          <v-text-field
                  label="SCoins"
                  v-model.number="poolSCoinGM"
                  :rules="posNumRule"
                  required
                  type="number"
                  :readonly="limits.played"
          ></v-text-field>
        </v-flex>
      </v-layout>
    </div>
    <v-layout row>
      <v-spacer></v-spacer>
      <v-btn round color="warning" @click="submit" :disabled="!validation">next</v-btn>
    </v-layout>
  </v-form>
</template>

<script>
    import {mapGetters, mapState} from "vuex";
    import ImageInput from "@/components/ImageInput";
    import {createHelpers} from 'vuex-map-fields';
    import BracketItem from "@/components/BracketItem";

    const {mapFields: mapFormFields} = createHelpers({
        getterType: 'instaplayForm/winner/getWinnerField',
        mutationType: 'instaplayForm/winner/updateWinnerField',
    });
    const {mapFields: mapSimpleFields} = createHelpers({
        getterType: 'instaplayForm/winner/getSimpleField',
        mutationType: 'instaplayForm/winner/updateSimpleField',
    });
    const {mapFields: mapPoolFields} = createHelpers({
        getterType: 'instaplayForm/winner/getPoolField',
        mutationType: 'instaplayForm/winner/updatePoolField',
    });
    const {mapFields: mapTotalFields} = createHelpers({
        getterType: 'instaplayForm/winner/getTotalField',
        mutationType: 'instaplayForm/winner/updateTotalField',
    });
    const {mapFields: mapLineFields} = createHelpers({
        getterType: 'instaplayForm/winner/getLineField',
        mutationType: 'instaplayForm/winner/updateLineField',
    });
    const {mapFields: mapSpreadFields} = createHelpers({
        getterType: 'instaplayForm/winner/getSpreadField',
        mutationType: 'instaplayForm/winner/updateSpreadField',
    });
    const {mapFields: mapBracketFields} = createHelpers({
      getterType: 'instaplayForm/winner/getBracketField',
      mutationType: 'instaplayForm/winner/updateBracketField',
    });
    export default {
        name: "Winner",
        components: {BracketItem, ImageInput},
        data() {
            return {
                valid: false,
                posNumRule: [v => !!v || v===0 || 'required', v => v>=0 || 'May not be less than zero'],
                customImage: {
                    name: '',
                    url: '',
                    file: '',
                },
                wrongTry: false,
                bracketModel: {}
            }
        },
        methods: {
            submit: function () {
                this.$emit('setState', 5)
                //submit the form...
            },
            update: function (file, obj) {
                if (file) {
                    obj.file = file // this file can be sent to server...
                    obj.name = file.name
                    const fr = new FileReader()
                    fr.readAsDataURL(file)
                    fr.addEventListener('load', () => {
                        obj.url = fr.result
                    })
                } else {
                    obj.url = ''
                    obj.name = ''
                    obj.file = ''
                }
            }
        },
        computed: {
            ...mapFormFields(['type']),
            ...mapSimpleFields(['prize', 'winSCoins', 'winnerSelection', 'numWinners', 'noLimit', 'hashtag', 'description', 'image']),
            ...mapPoolFields(['wagerCoins', 'minCoinsToPlay', 'maxCoinsToPlay']),
            ...mapPoolFields({poolSCoinGM: 'SCoinGM'}),
            ...mapTotalFields(['totalpoints', 'wagerSCoins']),
            ...mapTotalFields({totalWinScoins: 'winSCoins', totalSCoinGM: 'SCoinGM'}),
            ...mapLineFields(['SCoinFavored', 'SCoinUnderdog', 'SCoinGM']),
            ...mapSpreadFields(['pointSpread']),
            ...mapSpreadFields({
                spreadWagerSCoins: 'wagerSCoins',
                spreadWinSCoins: 'winSCoins',
                spreadSCoinGM: 'SCoinGM'
            }),
            ...mapBracketFields(['winnerLabel']),
            ...mapGetters('instaplayForm/contestants', ['cards', 'numberOfCards', 'favoredContestant', 'underdogContestant']),
            ...mapState('instaplayForm', ['limits']),
            validation: function () {
                return this.valid
                    && (this.type !== this.typeItems[0].value || !!this.winnerSelection)
                    && (this.poolConstraint || this.minCoinsToPlay === this.maxCoinsToPlay)
                    && (this.type !== 'bracket' || this.cards.every(x => this.bracketModel[x.id] !== "") )
            },
            typeItems: function () {
                return [{text: 'Simple', value: 'simple'},
                    {text: 'Pool', value: 'pool'},
                    {text: `Total points ${this.numberOfCards !== 2 ? '(requires 2 contestants)' : ''}`, value: 'total', disabled: this.numberOfCards !== 2},
                    {text: `SCoin Line  ${this.numberOfCards !== 2 ? '(requires 2 contestants)' : ''}`, value: 'line', disabled: this.numberOfCards !== 2},
                    {text: `Point Spread  ${this.numberOfCards !== 2 ? '(requires 2 contestants)' : ''}`, value: 'spread', disabled: this.numberOfCards !== 2},
                    {
                      text: `Bracket ${this.numberOfCards !== 4 && this.numberOfCards !== 8 && this.numberOfCards !== 16 && this.numberOfCards !== 32 ? '(requires 4, 8, 16, or 32 contestants)': ''}`,
                      value: 'bracket',
                      disabled: this.numberOfCards !== 4 && this.numberOfCards !== 8 && this.numberOfCards !== 16 && this.numberOfCards !== 32
                    }
                    ]
            },
            poolConstraint: function () {
                return (this.type !== this.typeItems[1].value ||
                    (this.minCoinsToPlay <= this.maxCoinsToPlay) ||
                    (!this.minCoinsToPlay || !this.maxCoinsToPlay)) &&
                    (!this.wrongTry || this.minCoinsToPlay !== this.maxCoinsToPlay)
            },
            customImageUrl: function () {
                return this.customImage.url
            }
        },
        watch: {
            bracketModel: {
              handler: function() {
                this.$store.commit("instaplayForm/winner/setBracketOrder", this.bracketModel)
              },
              deep: true
            },
          validation: function (val) {
                this.$emit('input', val)
            },
            numberOfCards: function (val) {
                if (val !== 2)
                    if (this.type !== this.typeItems[0].value && this.type !== this.typeItems[1].value)
                        this.type = ''
            },
            customImageUrl: function (val) {
                this.image = val
            },
            minCoinsToPlay: function (val) {
                if (this.maxCoinsToPlay === '' || this.maxCoinsToPlay < val) {
                    this.maxCoinsToPlay = val
                }
            }
        }
    }
</script>

<style>
  .v-input--selection-controls .v-input__control {
    width: 100% !important;
  }
</style>
